import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FiPlus, FiSave, FiTrash2, FiCheckCircle, FiUsers } from "react-icons/fi";
import {
  addWeeklyPreparationItem,
  deleteWeeklyPreparationItem,
  fetchWeeklyPreparation,
  updateWeeklyPreparation,
  updateWeeklyPreparationItem,
} from "../../store/devotionSlice";

// -------- Glass surfaces (shared vocabulary with ReadingPlansHub) ---------

const glassPanel = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.15) 100%)",
  backdropFilter: "blur(22px) saturate(108%)",
  WebkitBackdropFilter: "blur(22px) saturate(108%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 18px 44px rgba(55,79,99,0.13), inset 0 1px 0 rgba(255,255,255,0.55)",
};
const innerTranslucent = {
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.35))",
  border: "1px solid rgba(255,255,255,0.45)",
};
const primaryPill =
  "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed";
const secondaryPill =
  "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-full bg-white/60 text-neu-primary text-label-md font-semibold border border-white/60 hover:bg-white/80 active:bg-white/40 transition-all";
const dangerPill =
  "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-full bg-white/60 text-[#b02a37] text-label-md font-semibold border border-white/60 hover:bg-white/80 active:bg-white/40 transition-all";

// -------- Date helpers ----------------------------------------------------

const isoDate = (date) => date.toISOString().slice(0, 10);
const currentSunday = () => {
  const day = new Date();
  const diff = day.getDay();
  day.setDate(day.getDate() - diff);
  return isoDate(day);
};
const spellWeek = (dateISO, language) => {
  if (!dateISO) return "";
  const locale = language === "ko" ? "ko-KR" : "en-US";
  return new Date(`${dateISO}T00:00:00`).toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const firstEntryText = (entry) =>
  entry.stands_out ||
  entry.god_is_showing ||
  entry.response ||
  entry.prayer ||
  entry.diary ||
  "";

// -------- Root component --------------------------------------------------

const SundayPreparationPage = () => {
  const { weekStart } = useParams();
  const selectedWeek = weekStart || currentSunday();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith("ko") ? "ko" : "en";
  const payload = useSelector((state) => state.devotions.weeklyPreparation);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const [draft, setDraft] = useState({
    weekly_summary: "",
    prayer_request: "",
    is_ready: false,
    revision: null,
  });
  const [itemDrafts, setItemDrafts] = useState({});

  useEffect(() => {
    dispatch(fetchWeeklyPreparation(selectedWeek));
  }, [dispatch, selectedWeek]);

  useEffect(() => {
    if (!payload?.preparation) return;
    setDraft({
      weekly_summary: payload.preparation.weekly_summary || "",
      prayer_request: payload.preparation.prayer_request || "",
      is_ready: Boolean(payload.preparation.is_ready),
      revision: payload.preparation.revision,
    });
    setItemDrafts(
      Object.fromEntries(
        (payload.preparation.items || []).map((item) => [item.id, item.share_text])
      )
    );
  }, [payload]);

  const entriesByPassage = useMemo(() => payload?.entries || [], [payload]);
  const items = payload?.preparation?.items || [];

  const saveWeek = () => {
    dispatch(updateWeeklyPreparation({ weekStart: selectedWeek, preparation: draft }));
  };

  const addItem = (entry) => {
    const shareText = firstEntryText(entry);
    if (!shareText) return;
    dispatch(
      addWeeklyPreparationItem({
        weekStart: selectedWeek,
        devotionEntryId: entry.id,
        shareText,
      })
    );
  };

  const passageRef = (passage) =>
    language === "ko"
      ? passage?.reference_ko || passage?.reference_en
      : passage?.reference_en || passage?.reference_ko;

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors overflow-x-hidden"
    >
      <main className="w-full">
        <div className="w-full max-w-[1200px] mx-auto px-margin-mobile md:px-margin-neu py-space-lg md:py-space-xl flex flex-col gap-6 md:gap-space-lg">
          {/* ------ Header ------ */}
          <div className="flex flex-col gap-space-xs">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Personal · Weekly Rhythm
            </span>
            <h1 className="font-noto-serif text-headline-lg text-neu-primary">
              Prepare for Sunday
            </h1>
            <p className="text-body-md text-on-surface-variant max-w-xl">
              Bring what stood out from this week into what you'll share on Sunday. Private
              until you say otherwise.
            </p>
            <p className="text-label-md text-on-surface-variant mt-1">
              Week of{" "}
              <span className="font-semibold text-neu-primary">
                {spellWeek(selectedWeek, language)}
              </span>
            </p>
          </div>

          {/* ------ Two-column: entries (left) + excerpts (right) ------ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-gutter">
            {/* This Week's entries */}
            <section
              className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
              style={glassPanel}
              aria-label="This week's reflections"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                  This Week
                </h2>
                <span className="text-label-sm text-on-surface-variant">
                  {entriesByPassage.length} reflection
                  {entriesByPassage.length === 1 ? "" : "s"}
                </span>
              </div>

              {entriesByPassage.length ? (
                <ul className="flex flex-col gap-space-sm">
                  {entriesByPassage.map(({ entry, passage }) => {
                    const text = firstEntryText(entry);
                    return (
                      <li
                        key={entry.id}
                        className="rounded-[1rem] p-space-sm flex flex-col gap-space-xs"
                        style={innerTranslucent}
                      >
                        <div className="flex items-baseline justify-between flex-wrap gap-2">
                          <span className="font-noto-serif text-headline-sm text-neu-primary">
                            {passageRef(passage) || "Passage"}
                          </span>
                          <span className="text-label-sm text-on-surface-variant">
                            {passage.passage_date}
                          </span>
                        </div>
                        <p className="text-body-md text-neu-primary whitespace-pre-wrap line-clamp-4">
                          {text || "No reflection text yet."}
                        </p>
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => addItem(entry)}
                            disabled={!text}
                            className={`${primaryPill} w-full sm:w-auto`}
                          >
                            <FiPlus aria-hidden="true" />
                            Copy Excerpt
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-body-md text-on-surface-variant">
                  No private entries were found for this week.
                </p>
              )}
            </section>

            {/* Selected excerpts */}
            <section
              className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
              style={glassPanel}
              aria-label="Selected excerpts"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                  Selected Excerpts
                </h2>
                <span className="text-label-sm text-on-surface-variant">
                  {items.length} selected
                </span>
              </div>

              {items.length ? (
                <ul className="flex flex-col gap-space-sm">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className="rounded-[1rem] p-space-sm flex flex-col gap-space-xs"
                      style={innerTranslucent}
                    >
                      <textarea
                        value={itemDrafts[item.id] || ""}
                        onChange={(event) =>
                          setItemDrafts((current) => ({
                            ...current,
                            [item.id]: event.target.value,
                          }))
                        }
                        rows={4}
                        className="w-full rounded-[0.75rem] bg-white/70 border border-white/60 p-3 text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none focus:bg-white/90 transition-colors resize-y"
                      />
                      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-space-sm">
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              updateWeeklyPreparationItem({
                                weekStart: selectedWeek,
                                itemId: item.id,
                                item: { share_text: itemDrafts[item.id] || "" },
                              })
                            )
                          }
                          className={`${secondaryPill} flex-1 sm:flex-none min-w-0 whitespace-nowrap px-3`}
                        >
                          <FiSave aria-hidden="true" />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              deleteWeeklyPreparationItem({
                                weekStart: selectedWeek,
                                itemId: item.id,
                              })
                            )
                          }
                          className={`${dangerPill} flex-1 sm:flex-none min-w-0 whitespace-nowrap px-3`}
                        >
                          <FiTrash2 aria-hidden="true" />
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-body-md text-on-surface-variant">
                  No excerpts selected yet. Copy a reflection from the left to start.
                </p>
              )}
            </section>
          </div>

          {/* ------ Weekly Summary ------ */}
          <section
            className="rounded-[1.5rem] p-space-md md:p-space-lg flex flex-col gap-space-md"
            style={glassPanel}
            aria-label="Weekly summary"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-noto-serif text-headline-sm text-neu-primary">
                Weekly Summary
              </h2>
              {draft.is_ready && (
                <span
                  className="inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm text-accent-cta font-semibold"
                  style={innerTranslucent}
                >
                  <FiCheckCircle /> Ready
                </span>
              )}
            </div>

            <div className="grid gap-space-md">
              <label className="grid gap-1">
                <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                  Summary
                </span>
                <textarea
                  value={draft.weekly_summary}
                  onChange={(event) =>
                    setDraft({ ...draft, weekly_summary: event.target.value })
                  }
                  rows={4}
                  className="w-full rounded-[0.75rem] bg-white/70 border border-white/60 p-3 text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none focus:bg-white/90 transition-colors resize-y"
                  placeholder="What did God show you this week?"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                  Prayer Request
                </span>
                <textarea
                  value={draft.prayer_request}
                  onChange={(event) =>
                    setDraft({ ...draft, prayer_request: event.target.value })
                  }
                  rows={3}
                  className="w-full rounded-[0.75rem] bg-white/70 border border-white/60 p-3 text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none focus:bg-white/90 transition-colors resize-y"
                  placeholder="What are you asking prayer for?"
                />
              </label>
              <label className="inline-flex items-center gap-3 text-body-md text-neu-primary">
                <input
                  type="checkbox"
                  checked={draft.is_ready}
                  onChange={(event) =>
                    setDraft({ ...draft, is_ready: event.target.checked })
                  }
                  className="h-5 w-5 accent-[color:var(--accent-cta)]"
                />
                Mark ready to share on Sunday
              </label>
            </div>

            <div>
              <button
                type="button"
                onClick={saveWeek}
                className={`${primaryPill} w-full sm:w-auto`}
              >
                <FiUsers aria-hidden="true" />
                Save Preparation
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default SundayPreparationPage;
